/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { jsx } from '@emotion/core';
import { useContext } from 'react';
import IntegrationContext from './IntegrationContext';
import useGoogleButtons from '../useGoogleButtons';
import GetGoogleFormButtons from '../queries/GetGoogleFormButtons';
import DestroyGoogleFormButton from './mutations/DestroyGoogleFormButton';
import { useMutation } from '@apollo/react-hooks';
import ExternalLink from './ExternalLink';
import OverlayFactory from 'comps/OverlayFactory';
import { Tooltip } from 'react-bootstrap';
import intCommands from './commands';

const refetchQueries = [{ query: GetGoogleFormButtons }];

const TooltipWrapper = OverlayFactory(Tooltip);

export default (): null | Element<string> => {
  const intCtx = useContext(IntegrationContext);
  const { data, error, loading } = useGoogleButtons();
  const opts = { refetchQueries };
  const [destroy] = useMutation(DestroyGoogleFormButton, opts);

  if (error) return <div className="text-danger">{error.message}</div>;
  if (loading) return <i className="fa fa-spinner fa-spin" />;
  if (data.length === 0) return null;

  return (
    <div data-testid="IntegrationList">
      <h4 className="mb-3">Buttons</h4>
      {data.map((config, i) => (
        <div
          className="border border-info d-lg-flex p-4 rounded mb-2 align-items-center"
          key={i}
        >
          <div className="text-center text-lg-left">
            <ExternalLink
              href={config.url}
              className="text-info rounded p-2 d-inline-block border border-info font-weight-bold"
              data-testid={`ConfigName-${config.id}`}
            >
              {config.name} <i className="fa fa-external-link-alt" />
            </ExternalLink>{' '}
            <TooltipWrapper
              placement="right"
              content={`Appears on ${config.resourceName}`}
              id="appears-on-tooltip"
              className="d-none d-lg-block"
            >
              <i className="ml-2 fa text-info fa-info-circle far fa-info" />
            </TooltipWrapper>
          </div>
          <div className="ml-auto font-weight-bold mt-4 mt-lg-0 text-center text-lg-left">
            <a
              className="d-inline-block text-info"
              tabIndex={0}
              data-testid={`EditConfig-${config.id}`}
              onClick={() => {
                intCommands(intCtx).editIntegration(config);
              }}
            >
              Edit <i className="fa fa-edit fa-fixed" />
            </a>
            <span className="ml-2 mr-2 text-info">|</span>
            <a
              className="d-inline-block text-info"
              tabIndex={0}
              data-testid={`DeleteConfig-${config.id}`}
              onClick={() => {
                const yes = confirm('Are you sure?');
                if (yes) {
                  destroy({ variables: { id: config.id } }).then((res) => {
                    if (res.data.googleFormButtonDestroy.success) {
                      intCommands(intCtx).finish();
                    } else {
                      // TODO: handle errors
                    }
                  });
                }
              }}
            >
              Remove <i className="fa fa-trash fa-fixed" />
            </a>
          </div>
        </div>
      ))}
    </div>
  );
};
